/**
 * PROTOTYPE spread seed + factory functions -- THROWAWAY.
 *
 * New/duplicate/apply-to-all spread builders and the example project the prototype opens
 * with. Split out of spreadSplitData to keep that module focused on state + derivations.
 */
import {
  Spread, ProjectState, SpreadOverrides, LengthUnit, ThroughputMode, MachineKey, computedJoints, DEFAULT_ENABLED,
} from './spreadSplitData';
import { ModuleSource, newSource } from './spreadSourceData';
import { DEFAULT_MT_OPTIONS } from './spreadMaterialData';
import { DEFAULT_PLATFORM_OPTIONS, DEFAULT_RETENTION_OPTIONS } from './spreadProjectOptions';

const GEOMETRY_DEFAULTS = {
  lengthUnit: 'mi' as LengthUnit, pipeLengthFt: 40,
  throughputMode: 'perDay' as ThroughputMode, jointsPerDay: 60, durationMonths: 24,
};

// One source per volume-driven module, each seeded at the spread's joint count.
function seededSources(jointCount: number): Pick<Spread, 'mtMills' | 'ndtSources' | 'coatingSources'> {
  return {
    mtMills: [newSource('Mill', 1, jointCount)],
    ndtSources: [newSource('Vendor', 1, jointCount)],
    coatingSources: [newSource('Contractor', 1, jointCount)],
  };
}

export function newSpread(index: number): Spread {
  const s: Spread = {
    id: `s${index}-${index * 7 + 3}`,
    name: `Spread ${index}`,
    lengthValue: 0, jointsOverride: null,
    ...GEOMETRY_DEFAULTS,
    machines: { p625: 0, p600z: 0, m500: 0, m300: 0, manual: 0, iwm: 0 },
    mtMills: [], mtOptions: { ...DEFAULT_MT_OPTIONS }, ndtSources: [], coatingSources: [],
    platformOptions: { ...DEFAULT_PLATFORM_OPTIONS }, retentionOptions: { ...DEFAULT_RETENTION_OPTIONS },
    enabled: { ...DEFAULT_ENABLED },
    overrides: {},
  };
  return { ...s, ...seededSources(computedJoints(s)) };
}

/** Clone a spread as a new one (keeps its geometry + config). */
export function duplicateSpread(project: ProjectState, sourceId: string): ProjectState {
  const src = project.spreads.find((s) => s.id === sourceId);
  if (!src) return project;
  const clone = (sources: ModuleSource[]) => sources.map((m) => ({ ...m }));
  const copy: Spread = {
    ...src, id: `dup-${sourceId}-${project.spreads.length}`, name: `Spread ${project.spreads.length + 1}`,
    mtMills: clone(src.mtMills), ndtSources: clone(src.ndtSources), coatingSources: clone(src.coatingSources),
    mtOptions: { ...src.mtOptions }, platformOptions: { ...src.platformOptions }, retentionOptions: { ...src.retentionOptions },
    enabled: { ...src.enabled },
    overrides: { ...src.overrides },
  };
  return { ...project, spreads: [...project.spreads, copy] };
}

/** Remove a spread. A project always keeps at least one spread, so the last is never deleted. */
export function deleteSpread(project: ProjectState, id: string): ProjectState {
  if (project.spreads.length <= 1) return project;
  return { ...project, spreads: project.spreads.filter((s) => s.id !== id) };
}

/** Copy one spread's CONFIG to every other spread, preserving each target's own length. */
export function applyConfigToAll(project: ProjectState, sourceId: string): ProjectState {
  const src = project.spreads.find((s) => s.id === sourceId);
  if (!src) return project;
  return {
    ...project,
    spreads: project.spreads.map((s) => {
      if (s.id === sourceId) return s;
      const t: Spread = {
        ...s,
        pipeLengthFt: src.pipeLengthFt,
        throughputMode: src.throughputMode, jointsPerDay: src.jointsPerDay, durationMonths: src.durationMonths,
        machines: { ...src.machines },
        mtMills: [], mtOptions: { ...src.mtOptions }, ndtSources: [], coatingSources: [],
        platformOptions: { ...src.platformOptions }, retentionOptions: { ...src.retentionOptions },
        enabled: { ...src.enabled },
        overrides: JSON.parse(JSON.stringify(src.overrides)),
      };
      return { ...t, ...seededSources(computedJoints(t)) }; // one source each = this spread's own joints
    }),
  };
}

// Seed roughly matching the sales team's example (lengths chosen to reproduce the joint counts).
// Spread 2 carries an example override (thinner NDT margin) so the drawer shows one live.
const base = (id: string, name: string, mi: number, machines: Record<MachineKey, number>, overrides: SpreadOverrides): Spread => {
  const s: Spread = {
    id, name, lengthValue: mi, jointsOverride: null, ...GEOMETRY_DEFAULTS,
    machines, mtMills: [], mtOptions: { ...DEFAULT_MT_OPTIONS }, ndtSources: [], coatingSources: [],
    platformOptions: { ...DEFAULT_PLATFORM_OPTIONS }, retentionOptions: { ...DEFAULT_RETENTION_OPTIONS },
    enabled: { ...DEFAULT_ENABLED }, overrides,
  };
  return { ...s, ...seededSources(computedJoints(s)) };
};

export const initialProject: ProjectState = {
  customerName: 'Northern Pipeline Co.',
  projectTitle: 'Trunkline Phase 2',
  refNumber: 'PROP-2026-014',
  reporting: 'spread',
  config: {},
  services: { enabled: false, supportHours: 80, trainingTier: 'medium', provisioningHours: 24, onFieldDays: 6 },
  spreads: [
    base('s1', 'Spread 1', 45, { p625: 6, p600z: 0, m500: 2, m300: 0, manual: 2, iwm: 0 }, {}),
    base('s2', 'Spread 2', 54, { p625: 6, p600z: 0, m500: 2, m300: 0, manual: 2, iwm: 0 }, { config: { ndt: { marginTarget: 42 } } }),
    base('s3', 'Spread 3', 66, { p625: 6, p600z: 0, m500: 2, m300: 0, manual: 2, iwm: 0 }, {}),
    base('s4', 'Spread 4', 23, { p625: 0, p600z: 0, m500: 2, m300: 0, manual: 4, iwm: 1 }, {}),
  ],
};
